/**
 * Dependencies section inside the issue detail panel.
 *
 * The keyboard-accessible half of the feature: the Gantt's drag is faster, but
 * it is a pointer gesture with no equivalent, and an issue's dependencies have
 * to be readable from the issue itself rather than only from a chart.
 *
 * Both directions are shown. "Waiting on" is what has to happen first;
 * "Drives" is what moves when this issue moves — and seeing the second before
 * dragging a date is the point.
 *
 * A row is a line you READ (see `DependencyRow`); the editor opens on click.
 * One row at a time, so the section never becomes a wall of forms.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Pin, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";

import type {
  IssueDependencyHardness,
  IssueDependencyType,
  PMIssue,
  PMIssueDependency,
} from "@/domains/pm/types";
import { useIssuesStore } from "@/stores/pm/hooks/usePMStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { friendlyIssueError } from "@/domains/pm/utils/issueErrors";
import { useIssueDependencies } from "@/domains/pm/hooks/useIssueDependencies";
import { useScheduleStaging } from "./schedule/scheduleStagingContext";
import {
  isEdgeStaged,
  overlayDependencies,
  type EdgePatch,
} from "@/domains/pm/hooks/useStagedSchedule";
import {
  createIssueDependency,
  deleteIssueDependency,
  updateIssueDependency,
} from "@/stores/pm/repository";
import { PanelSectionLabel } from "./PanelSectionLabel";
import { DependencyRow } from "./dependencies/DependencyRow";
import { DependencyDraftRow } from "./dependencies/DependencyDraftRow";
import { InfoHint } from "./dependencies/InfoHint";
import {
  DependencyPickerDialog,
  type DependencyDirection,
} from "./dependencies/DependencyPickerDialog";

const pairKey = (left: number, right: number) =>
  `${Math.min(left, right)}:${Math.max(left, right)}`;

export const IssueDependenciesSection = observer(function IssueDependenciesSection({
  issue,
  readOnly = false,
  hideHeading = false,
  onPickerOpenChange,
}: {
  issue: PMIssue;
  /** Read the graph without being able to change it — snapshot mode. */
  readOnly?: boolean;
  /** The panel field already labels this; do not label it twice. */
  hideHeading?: boolean;
  /** Told when the add dialog opens or closes, so a popover holding this
   *  section can stay open while focus is in the dialog. */
  onPickerOpenChange?: (open: boolean) => void;
}) {
  const { t } = useLanguage();
  const copy = t.pm.issues.dependencies;
  const issuesStore = useIssuesStore();
  const queryClient = useQueryClient();
  const { dependencies } = useIssueDependencies(issue.building_id);
  const staging = useScheduleStaging();
  /** Which row has its editor open. One at a time. */
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  /** An issue chosen in the picker, still waiting for a relationship. */
  const [draft, setDraft] = useState<{
    candidate: PMIssue;
    direction: DependencyDirection;
  } | null>(null);
  const draftGeneration = useRef(0);
  const creating = useRef(new Set<string>());
  const [pendingPairs, setPendingPairs] = useState<Set<string>>(() => new Set());

  const clearDraft = () => {
    draftGeneration.current += 1;
    setDraft(null);
  };

  const byId = useMemo(
    () => new Map<number, PMIssue>(issuesStore.issues.map((i) => [i.id, i])),
    [issuesStore.issues]
  );

  // With edits staged, the panel shows the arrangement the Gantt is drawing —
  // not the saved graph, which would contradict the chart beside it.
  const overlaid = useMemo(
    () => (staging ? overlayDependencies(dependencies.all, staging.staged) : dependencies.all),
    [dependencies, staging]
  );
  const incoming = useMemo(
    () => overlaid.filter((d) => d.successor_issue_id === issue.id),
    [overlaid, issue.id]
  );
  const outgoing = useMemo(
    () => overlaid.filter((d) => d.predecessor_issue_id === issue.id),
    [overlaid, issue.id]
  );

  // Staged when the Gantt is holding an arrangement back, written immediately
  // otherwise — the same fork the date controls take.
  const changeEdge = (dep: PMIssueDependency, patch: EdgePatch) => {
    if (staging) {
      staging.stageEdgeEdit(dep.id, patch);
      return;
    }
    void run(() => updateIssueDependency(dep.id, patch));
  };

  const removeEdge = (dep: PMIssueDependency) => {
    if (expandedId === dep.id) setExpandedId(null);
    if (staging) {
      staging.stageEdgeDrop(dep.id);
      return;
    }
    void run(() => deleteIssueDependency(dep.id));
  };

  // Picking an issue does NOT write an edge: it opens a draft row whose editor
  // is waiting for a relationship. Nothing is saved until that is chosen, so no
  // edge ever carries a relationship the database picked.
  const addEdge = (candidate: PMIssue, direction: DependencyDirection) => {
    if (creating.current.has(pairKey(issue.id, candidate.id))) return;
    draftGeneration.current += 1;
    setDraft({ candidate, direction });
  };

  const createFromDraft = (edge: {
    dep_type: IssueDependencyType;
    lag_days: number;
    hardness: IssueDependencyHardness;
  }) => {
    if (!draft) return;
    const generation = draftGeneration.current;
    const { candidate, direction } = draft;
    const pair = pairKey(issue.id, candidate.id);
    if (creating.current.has(pair)) return;
    const ends =
      direction === "waiting"
        ? { predecessor_issue_id: candidate.id, successor_issue_id: issue.id }
        : { predecessor_issue_id: issue.id, successor_issue_id: candidate.id };
    if (staging) {
      clearDraft();
      setExpandedId(staging.stageEdgeAdd({ ...ends, ...edge }));
      return;
    }
    creating.current.add(pair);
    setPendingPairs(new Set(creating.current));
    void run(async () => {
      const created = await createIssueDependency({ ...ends, ...edge });
      if (draftGeneration.current === generation) {
        clearDraft();
        setExpandedId(created.id);
      }
    }).finally(() => {
      creating.current.delete(pair);
      setPendingPairs(new Set(creating.current));
    });
  };

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["pm-issue-dependencies", issue.building_id] });

  const run = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      await refresh();
    } catch (e) {
      toast.error(friendlyIssueError(e, t));
    }
  };

  /**
   * Candidates for a new link: live issues in the SAME building project, minus
   * this one and anything already linked either way. Cross-project links are
   * refused by the database, so offering one would be a trap.
   */
  const candidates = useMemo(() => {
    const linked = new Set([
      ...incoming.map((d) => d.predecessor_issue_id),
      ...outgoing.map((d) => d.successor_issue_id),
    ]);
    return issuesStore.issues.filter(
      (i) =>
        i.id !== issue.id &&
        i.building_project_id === issue.building_project_id &&
        !linked.has(i.id)
    );
  }, [issuesStore.issues, issue.id, issue.building_project_id, incoming, outgoing]);

  const canAdd = candidates.length > 0 && !readOnly;
  const pickerCandidates = candidates.filter(
    (candidate) => !pendingPairs.has(pairKey(issue.id, candidate.id))
  );
  const canPick = pickerCandidates.length > 0 && !readOnly;

  useEffect(() => {
    setPickerOpen(false);
    draftGeneration.current += 1;
    setDraft(null);
  }, [issue.id, canAdd]);

  useEffect(() => {
    onPickerOpenChange?.(pickerOpen && canPick);
    return () => onPickerOpenChange?.(false);
  }, [pickerOpen, canPick, onPickerOpenChange]);

  if (
    incoming.length === 0 &&
    outgoing.length === 0 &&
    !draft &&
    (candidates.length === 0 || readOnly)
  ) {
    return hideHeading ? <p className="text-xs text-muted-foreground">{copy.empty}</p> : null;
  }

  const total = incoming.length + outgoing.length;

  /**
   * Everything a row needs to draw itself, for one direction.
   *
   * It used to flag an edge whose successor starts earlier than the edge
   * allows. That warning is gone: the cascade already raises it at Confirm,
   * where it is actionable, and here it made a perfectly ordinary edge look
   * like a fault the moment someone dragged a bar.
   */
  const rowsFor = (edges: PMIssueDependency[], direction: DependencyDirection) =>
    edges.map((dep) => ({
      dep,
      other: byId.get(direction === "waiting" ? dep.predecessor_issue_id : dep.successor_issue_id),
      direction,
      staged: staging ? isEdgeStaged(dep.id, staging.staged) : false,
    }));

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <PanelSectionLabel>
          {copy.title}
          {total > 0 && <span> · {total}</span>}
        </PanelSectionLabel>
        {canPick && total > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setPickerOpen(true)}
            className="h-7 gap-1.5 px-2 text-xs font-medium text-primary hover:bg-accent hover:text-primary"
          >
            <Plus className="h-3.5 w-3.5" />
            {copy.add}
          </Button>
        )}
      </div>

      {canPick && (
        <DependencyPickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          candidates={pickerCandidates}
          onAdd={addEdge}
        />
      )}

      {/* Pinning only means anything to an issue something else drives, so it
          is stated here rather than beside the dates -- where it would be a
          badge on most issues explaining nothing. */}
      {incoming.length > 0 && issue.schedule_pinned && (
        <div className="flex items-start gap-2 rounded-md border border-dashed border-primary/40 px-2.5 py-2 text-xs text-foreground/80">
          <Pin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
          <span className="flex-1">{copy.pinnedHint}</span>
          <button
            type="button"
            className="shrink-0 rounded px-1.5 py-0.5 font-medium text-primary hover:bg-accent"
            onClick={() =>
              void run(async () => {
                issuesStore.updateIssueOptimistic(issue.id, { schedule_pinned: false });
                await issuesStore.updateIssue(issue.id, { schedule_pinned: false });
              })
            }
          >
            {copy.unpin}
          </button>
        </div>
      )}

      {draft && (
        <ul>
          <DependencyDraftRow
            key={draftGeneration.current}
            candidate={draft.candidate}
            direction={draft.direction}
            onCreate={createFromDraft}
            onDiscard={clearDraft}
            pending={pendingPairs.has(pairKey(issue.id, draft.candidate.id))}
          />
        </ul>
      )}

      {total === 0 && !draft && (
        <div className="space-y-2.5 py-1">
          <p className="text-xs leading-snug text-muted-foreground">{copy.empty}</p>
          {canPick && (
            <Button type="button" size="sm" onClick={() => setPickerOpen(true)} className="gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              {copy.addDependency}
            </Button>
          )}
        </div>
      )}

      {incoming.length > 0 && (
        <DependencyGroup
          label={copy.waitingOn}
          info={copy.waitingOnInfo}
          icon={<ArrowLeft className="h-3 w-3" />}
          rows={rowsFor(incoming, "waiting")}
          readOnly={readOnly}
          expandedId={expandedId}
          onToggle={setExpandedId}
          onChange={changeEdge}
          onRemove={removeEdge}
        />
      )}

      {outgoing.length > 0 && (
        <DependencyGroup
          label={copy.drives}
          info={copy.drivesInfo}
          icon={<ArrowRight className="h-3 w-3" />}
          rows={rowsFor(outgoing, "drives")}
          readOnly={readOnly}
          expandedId={expandedId}
          onToggle={setExpandedId}
          onChange={changeEdge}
          onRemove={removeEdge}
        />
      )}
    </section>
  );
});

interface GroupRow {
  dep: PMIssueDependency;
  other: PMIssue | undefined;
  direction: DependencyDirection;
  staged: boolean;
}

function DependencyGroup({
  label,
  info,
  icon,
  rows,
  readOnly,
  expandedId,
  onToggle,
  onChange,
  onRemove,
}: {
  label: string;
  info: string;
  icon: React.ReactNode;
  rows: GroupRow[];
  readOnly: boolean;
  expandedId: number | null;
  onToggle: (id: number | null) => void;
  onChange: (dep: PMIssueDependency, patch: EdgePatch) => void;
  onRemove: (dep: PMIssueDependency) => void;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-foreground/70">
        <span className="text-primary">{icon}</span>
        {label}
        <InfoHint label={label}>
          <p>{info}</p>
        </InfoHint>
      </div>
      <ul className="space-y-0.5">
        {rows.map((row) => (
          <DependencyRow
            key={row.dep.id}
            {...row}
            readOnly={readOnly}
            expanded={expandedId === row.dep.id}
            onToggle={() => onToggle(expandedId === row.dep.id ? null : row.dep.id)}
            onChange={(patch) => onChange(row.dep, patch)}
            onRemove={() => onRemove(row.dep)}
          />
        ))}
      </ul>
    </div>
  );
}
